import { useState } from 'react';
import type { ReactNode } from 'react';
import { parseEntity, picksIn, sectionNamed } from '../entity/entity';
import type { Pick } from '../entity/entity';
import { mergedPicks } from '../entity/face';
import { recordingTitle, recordingWords, transcriptOf } from '../nook/nook';
import { PlayGlyph } from '../nook/parts';
import { Switches } from './DocPage';
import { Face } from './Face';
import { VaultAudio } from './VaultAudio';
import './doc.css';

/**
 * The play control, where the audio is not on this device — which on the web
 * is always, until audio reaches the vault. It stays, at full size, and says
 * so when pointed at, rather than vanishing: a recording whose bytes are
 * elsewhere must not read as a text note.
 */
export function Player({ size }: { size: number }) {
  return (
    <div className="player">
      <button
        type="button"
        className="player-btn"
        style={{ width: size, height: size }}
        aria-disabled="true"
        aria-label="Play — the audio is still on your phone"
      >
        <PlayGlyph />
      </button>
      <span className="player-note">The audio is still on your phone</span>
    </div>
  );
}

type Tab = 'transcript' | 'highlights' | 'extra';

/**
 * A recording read as a document — the same whether it opened over a nook or
 * was reached by a link: when it was said (or its name), the player, its face,
 * the switch between Transcript and Highlights, then whichever is open. The
 * switch sits above the long body, so the whole of it is never scrolled past
 * to reach the other.
 *
 * [onSetFace] makes the highlights pickable: a heart beside each puts it on
 * the face or takes it off, the same act as picking it on the phone.
 */
export function RecordingDoc({
  path,
  raw,
  anchor,
  extra,
  onSetFace,
}: {
  path: string;
  raw: string;
  /** A held topic, shown as the one the recording opened on. */
  anchor?: string;
  /** One more switch, where the surface has something of its own to show. */
  extra?: { label: string; render: () => ReactNode };
  onSetFace?: (picks: Pick[]) => void;
}) {
  const [tab, setTab] = useState<Tab>('transcript');
  const opening = recordingWords(path, raw, anchor);

  const options: { key: Tab; label: string }[] = [
    { key: 'transcript', label: 'Transcript' },
    { key: 'highlights', label: 'Highlights' },
    ...(extra ? [{ key: 'extra' as const, label: extra.label }] : []),
  ];

  return (
    <>
      <h1 className="doc-title">{recordingTitle(path, raw)}</h1>
      <VaultAudio notePath={path} size={48} fallback={<Player size={48} />} />
      <Face lines={opening.lines} picked={opening.picked} />
      <Switches options={options} open={tab} onOpen={(key) => key && setTab(key)} />
      {tab === 'transcript' && <Transcript path={path} raw={raw} anchor={anchor} />}
      {tab === 'highlights' && <Highlights path={path} raw={raw} onSetFace={onSetFace} />}
      {tab === 'extra' && extra?.render()}
    </>
  );
}

function Transcript({ path, raw, anchor }: { path: string; raw: string; anchor?: string }) {
  const topics = transcriptOf(path, raw);
  if (topics.length === 0) return <p className="doc-quiet">No transcript yet.</p>;
  return (
    <div className="doc-body is-soft doc-transcript">
      {topics.map((topic, i) => (
        <section key={topic.anchor || i} className={topic.anchor && topic.anchor === anchor ? 'held' : undefined}>
          {!topic.unnamed && <h3>{topic.title}</h3>}
          {topic.paragraphs.map((words, j) => (
            <p key={j}>{words}</p>
          ))}
        </section>
      ))}
    </div>
  );
}

/** Every candidate the recording was given, and anything picked by hand that
 *  never was one — in suggestion order, so toggling one moves only its mark. */
function Highlights({
  path,
  raw,
  onSetFace,
}: {
  path: string;
  raw: string;
  onSetFace?: (picks: Pick[]) => void;
}) {
  const entity = parseEntity(path, raw);
  const face = picksIn(sectionNamed(entity, 'face')?.body ?? '').filter(spoken);
  const suggestions = picksIn(sectionNamed(entity, 'suggestions')?.body ?? '').filter(spoken);
  const all = mergedPicks(face, suggestions);
  const onFace = new Set(face.map((p) => p.ref));
  if (all.length === 0) return <p className="doc-quiet">Nothing was highlighted in this one.</p>;

  return (
    <div className="doc-body doc-highlights">
      {all.map((pick) => (
        <p key={pick.ref} className={onFace.has(pick.ref) ? 'is-picked' : undefined}>
          {onSetFace && (
            <button
              type="button"
              className="doc-heart"
              aria-pressed={onFace.has(pick.ref)}
              aria-label={onFace.has(pick.ref) ? 'Take off the face' : 'Put on the face'}
              onClick={() =>
                onSetFace(onFace.has(pick.ref) ? face.filter((p) => p.ref !== pick.ref) : [...face, pick])
              }
            >
              <HeartGlyph filled={onFace.has(pick.ref)} />
            </button>
          )}
          {pick.text}
        </p>
      ))}
    </div>
  );
}

/** An old export listed the audio block among the picks, as a JSON payload. */
function spoken(pick: Pick): boolean {
  return !pick.text.trimStart().startsWith('{"');
}

function HeartGlyph({ filled }: { filled: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M6 10.5S1.2 7.6 1.2 4.4A2.6 2.6 0 0 1 6 3a2.6 2.6 0 0 1 4.8 1.4c0 3.2-4.8 6.1-4.8 6.1z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1"
      />
    </svg>
  );
}
